import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import DropZone from 'react-dropzone';
import { Menu } from 'primereact/menu';
import Moment from 'moment';

import DatePicker from '../../Common/DatePicker/DatePicker';
import Drawer from '../../Common/Drawer/Drawer';
import Notification from '../../Common/Notification';
import LoadingIndicator from '../../Common/LoadingIndicator/LoadingIndicator';
import Icon from '../../Common/Icon';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import { FILE_STATUSES, FEEDBACK_OPTIONS, CORE_STATUSES, EVENT_TYPE } from '../../../constants/constants';
import * as types from '../../../constants/actionTypes';
import { isValidUrl } from '../../../helpers/validation';
import { buildOutlookCalendarUrl, buildGoogleCalendarURL, buildMailToUrl, buildOutlookCalendarICSFile } from '../../../helpers/sharing';
import Modal from '../../Common/Modal/Modal';
import RadioGroup from '../../Common/RadioGroup/RadioGroup';
import TextArea from '../../Common/TextArea';
import detectBrowserLanguage from 'detect-browser-language';

import LimitedModeTaskDetailDrawerForm from './LimitedModeTaskDetailDrawerForm';
/**
 * Edit a limited portion of any type of task, whether created by pushing down
 * a corporate-created baseline task to a location or a task created by a user
 * directly at a single location.  Limited Mode only allows editing of certain
 * fields for two reason:
 *
 * 1) A Baseline-Created Location Task simply is not allowed to change any
 *    fields other than the ones allowed via this drawer.
 * 2) We save time/complexity by re-using these editing abilities on this
 *    drawer instead of trying to also include them in the LocationTask drawer.
 */

const SHARE_MENU_ITEMS = {
	AddToOutlook: {
		displayName: 'Add to Outlook Calendar',
		id: 'outlook',
	},
	AddToOutlookcom: {
		displayName: 'Add to Outlook.com Calendar',
		id: 'outlookcom',
	},
	AddToGoogle: {
		displayName: 'Add to Google Calendar',
		id: 'google',
	},
	MailTo: {
		displayName: 'Copy Task Info to Email',
		id: 'mailto',
	},
	ShareLink: {
		displayName: 'Share Link',
		id: 'shareLink',
	},
};

export class LimitedModeTaskDetailDrawer extends Component {
	constructor(props, context) {
		super(props, context);

		const defaultFile = {
			id: '0',
			fileName: '',
			name: '',
			description: '',
			isActive: true,
			isAdding: true,
			isEditing: false,
			editing_description: '',
			editing_fileStatusId: FILE_STATUSES.Pending,
		};

		this.state = {
			isShowingBasicInfo: false,
			projectCoaches: [],
			isLoadingProjectCoaches: false,
			isCommentsLoading: false,
			isEditStartDate: false,
			isEditTargetDate: false,
			isSaving: false,
			commentSampleSize: 10,
			isFormValid: true,
			isDropZoneActive: false,
			defaultFile: defaultFile,
			isSavingFile: false,
			isSavingLink: false,
			showFeedbackModal: false,
			feedbackSelection: 1,
			feedbackText: '',
			isFeedbackFormValid: true,
			isSubmittingFeedback: false,
			isCopyingToNewTicket: false,
			showUnsavedCommentModal: false,

			isTranslatingLocationTask: false,
			translationRating: 0,
			canTranslate: false,
			translationLabels: {},
		};
		this.dropzoneRef = React.createRef();
		this.menuParentRef = React.createRef();
	}

	componentDidUpdate(prevProps) {
		if (this.props.task.id !== prevProps.task.id && this.props.task.id !== 0) {
			this.validateTranslation();
		}
	}

	onToggleBasicInfo = () => {
		this.setState({
			isShowingBasicInfo: !this.state.isShowingBasicInfo,
		});
	};

	/**
	 * Private function that properly formats the new task
	 * before passing it up the component chain to the
	 * TaskDetailManager
	 */
	onTaskFieldChange = (name, value) => {
		const updatedTask = { ...this.props.task };
		updatedTask[name] = value;
		this.props.onTaskChange(updatedTask);
	};

	onStartDateChange = (date) => {
		this.onTaskFieldChange('startDate', date);
	};

	onTargetDateChange = (date) => {
		this.onTaskFieldChange('targetDate', date);
	};

	/**
	 * Steps
	 */
	onDragEnd = (result) => {
		// dropped outside the step list area
		if (!result.destination) {
			return;
		}

		const steps = Array.from(this.props.task.steps);
		const [removed] = steps.splice(result.source.index, 1);

		steps.splice(result.destination.index, 0, removed);

		// update the ordinal of any other steps after it in the array
		for (let i = 0; i < steps.length; i++) {
			steps[i].ordinal = i + 1;
		}

		this.onStepChange(steps);
	};

	onUpdateStepName = (step, e) => {
		let steps = this.props.task.steps.slice();
		const index = steps.findIndex((x) => x.ordinal === step.ordinal);
		step.name = e.target.value;
		steps.splice(index, 1, step);
		this.onStepChange(steps);
	};

	onStepChange = (steps) => {
		this.onTaskFieldChange('steps', steps);
	};

	toggleStepCompleted = (step) => {
		let steps = this.props.task.steps.slice();
		const index = steps.findIndex((x) => x.ordinal === step.ordinal);
		step.isCompleted = !step.isCompleted;
		steps.splice(index, 1, step);
		this.onStepChange(steps);
	};

	toggleStepEdit = (step) => {
		let steps = this.props.task.steps.slice();
		const index = steps.findIndex((x) => x.ordinal === step.ordinal);
		step.isEditing = !step.isEditing;
		steps.splice(index, 1, step);
		this.onStepChange(steps);
	};

	onAddStep = () => {
		const steps = this.props.task.steps.slice();
		const newStep = {
			id: 0,
			locationTaskId: this.props.task.id,
			name: '',
			isActive: true,
			ordinal: steps.length + 1,
			isEditing: true,
		};

		steps.push(newStep);
		this.onStepChange(steps);
	};

	toggleCertified = () => {
		const currentStatusId = this.props.task.statusId;
		const completeStatusId = this.props.filters.completeStatusId;
		const certified = this.props.task.isProjectCertified;

		// If the user wants to change certified to true but they didn't change status from completed
		if (certified === true && currentStatusId === completeStatusId) {
			return;
		}

		//if toggling to false
		if (certified === true) {
			this.onTaskFieldChange('isProjectCertified', false);
		} else {
			//if toggling to true
			const updatedTask = { ...this.props.task };
			updatedTask.isProjectCertified = true;
			updatedTask.statusId = completeStatusId;
			this.props.onTaskChange(updatedTask);
		}
	};

	/**
	 * Project Coach
	 */
	toggleEditProjectCoach = (e) => {
		e.preventDefault();
		this.loadProjectCoaches();
	};

	loadProjectCoaches = async () => {
		try {
			this.setState({ isLoadingProjectCoaches: true });
			this.onTaskFieldChange('isEditingProjectCoach', true);
			const response = await this.props.locationTaskActions.loadProjectCoaches(this.props.task.id);
			if (response.type === types.LOAD_PROJECT_COACHES_SUCCESS) {
				this.setState({ projectCoaches: response.payload.projectCoaches });
				this.setState({ isLoadingProjectCoaches: false });
			} else if (response.type === types.LOAD_PROJECT_COACHES_FAILURE) {
				Notification('error', 'Error', response.payload.message);
				this.setState({ isLoadingProjectCoaches: false });
			}
		} catch (error) {
			Notification('error', 'Error', error);
			this.props.onCancel();
		}
	};

	/**
	 * Save Task
	 */
	onSave = () => {
		if (this.validateForm()) {
			this.setState({ isFormValid: true });
			this.updateLocationTask();
		} else {
			this.setState({ isFormValid: false });
		}
	};

	validateForm = () => {
		const task = this.props.task;
		if (task.id === 0 || task.statusId === 0) {
			return false;
		}

		// start date must be before target date
		if (task.startDate && task.targetDate) {
			const start = Moment(task.startDate);
			const target = Moment(task.targetDate);
			const diffDays = Moment.duration(start.diff(target)).as('days');

			if (diffDays > 0) {
				Notification('error', '', 'The start date of a task must be on or before the target date.');
				return false;
			}
		}

		//only validated certified tasks that have been designated as such
		if (task.includeCertificationCheckbox === true) {
			if (task.isProjectCertified === true && task.statusId !== this.props.filters.completeStatusId) {
				Notification('error', 'Error', 'Task cannot be certified until the status is complete');
				return false;
			}

			if (task.isProjectCertified === false && task.statusId === this.props.filters.completeStatusId) {
				Notification('error', 'Error', 'Task cannot be completed until it has been certified');
				return false;
			}
		}

		return true;
	};

	updateLocationTask = async () => {
		try {
			// If a link is currently being edited, try to save it before saving the task
			if (this.anyLinksBeingEdited()) {
				const linkBeingEdited = this.props.task.links.filter((x) => x.isAdding || x.isEditing)[0];
				if (linkBeingEdited) {
					// If the link save failed, and it has shown its error message, halt the task save operation
					const linkSaveResponse = await this.onSaveLink(linkBeingEdited, true);
					if (!linkSaveResponse) {
						return;
					}
				}
			}

			// If a file is currently being edited, try to save it before saving the task
			if (this.anyFilesBeingEdited()) {
				const fileBeingEdited = this.props.task.files.filter((x) => x.isAdding || x.isEditing)[0];
				if (fileBeingEdited) {
					// If the file save failed, and it has shown its error message, halt the task save operation
					const fileSaveResponse = await this.onSaveFile(fileBeingEdited, true);
					if (!fileSaveResponse) {
						return;
					}
				}
			}

			// Save any pending comment before saving the task.
			// Doing this first keeps state consistent: if the comment fails,
			// neither the task nor the comment has been persisted yet.
			const commentSaved = await this.saveComment(false, true);
			if (!commentSaved) {
				return;
			}

			this.setState({ isSaving: true });

			//discard empty steps
			const steps = this.props.task.steps.filter((s) => s.name.trim().length > 0);

			for (let i = 0; i < steps.length; i++) {
				steps[i].ordinal = i + 1;
			}

			const request = {
				...this.props.task,
				steps: steps,
			};

			const response = await this.props.locationTaskActions.saveLocationTaskLimitedMode(request);

			if (response.type === types.UPDATE_LOCATION_TASK_LIMITED_MODE_SUCCESS) {
				// If status was set to core complete, show celebration confetti
				const completeStatus = this.props.filters.statuses.find(({ coreStatusId }) => coreStatusId === CORE_STATUSES.Complete);
				if (this.props.task.statusId === completeStatus.id) {
					const event = new Event(EVENT_TYPE.ConfettiCannon);
					document.dispatchEvent(event);
				}

				Notification('success', 'Task Updated', 'Task has been updated');
				this.props.onComplete();
			} else if (response.type === types.UPDATE_LOCATION_TASK_LIMITED_MODE_FAILURE) {
				Notification('error', 'Error', response.payload.message);
				this.setState({ isSaving: false });
			}
		} catch (error) {
			Notification('error', 'Error', error);
			this.setState({ isSaving: false });
		}
	};

	saveComment = async (suppressNotification = false, suppressRefresh = false) => {
		if (!this.props.task.comment || !this.props.task.comment.trim()) {
			return true;
		}
		try {
			const response = await this.props.locationTaskActions.createComment(this.props.task.id, this.props.task.comment);
			if (response.type === types.CREATE_LOCATION_TASK_COMMENT_SUCCESS) {
				this.onTaskFieldChange('comment', '');
				if (!suppressNotification) {
					Notification('success', 'Comment Created', '');
				}
				if (!suppressRefresh) {
					await this.props.loadComments(this.props.task.id, this.state.commentSampleSize);
				}
				return true;
			} else if (response.type === types.CREATE_LOCATION_TASK_COMMENT_FAILURE) {
				Notification('error', 'Error Saving Comment', response.payload.message);
				return false;
			}
		} catch (error) {
			Notification('error', 'Error Saving Comment', error);
			return false;
		}
	};

	onCancelClick = () => {
		if (this.props.task.comment && this.props.task.comment.trim()) {
			this.setState({ showUnsavedCommentModal: true });
		} else {
			this.props.onCancel();
		}
	};

	onDiscardCommentAndClose = () => {
		if (this.state.isCommentsLoading) {
			return;
		}
		this.setState({ showUnsavedCommentModal: false });
		this.onTaskFieldChange('comment', '');
		this.props.onCancel();
	};

	onSaveCommentAndClose = async () => {
		this.setState({ isCommentsLoading: true });
		const saved = await this.saveComment(false, false);
		this.setState({ isCommentsLoading: false });
		if (saved) {
			this.setState({ showUnsavedCommentModal: false });
			this.props.onCancel();
		}
	};

	saveCommentClick = async () => {
		if (!this.props.task.comment || !this.props.task.comment.trim()) {
			return;
		}
		this.setState({ isCommentsLoading: true });
		await this.saveComment(false, false);
		this.setState({ isCommentsLoading: false });
	};

	onShowMoreComments = () => {
		const newSampleSize = this.state.commentSampleSize + 10;
		this.setState({ commentSampleSize: newSampleSize });
		this.props.loadComments(this.props.task.id, newSampleSize);
	};

	onShowHistory = () => {
		this.props.loadHistory(this.props.task.id);
	};

	editStartDate = () => {
		this.setState({ isEditStartDate: true });
	};

	editTargetDate = () => {
		this.setState({ isEditTargetDate: true });
	};

	/**
	 * Files and Links Section
	 */
	anyLinksBeingEdited = () => {
		return this.props.task.links && this.props.task.links.filter((x) => x.isEditing || x.isAdding).length > 0;
	};

	setTaskLinks = (links) => {
		this.onTaskFieldChange('links', links);
	};

	setSavingLink = (isSavingLink) => {
		this.setState({ isSavingLink: isSavingLink });
	};

	onSaveLink = async (link, suppressSuccessNotification = false) => {
		let result = false;
		// Validate the temporary in-progress field values to see if they're valid
		const isLinkValid =
			link.editing_url &&
			link.editing_url.trim().length > 0 &&
			link.editing_name &&
			link.editing_name.trim().length > 0 &&
			isValidUrl(link.editing_url);

		if (isLinkValid) {
			// Set the temporary in-progress field values to the actual field values
			if (link.editing_url) {
				link.url = link.editing_url;
			}
			if (link.editing_name) {
				link.name = link.editing_name;
			}

			try {
				this.setState({ isSavingLink: true });

				if (link.isAdding) {
					const response = await this.props.locationTaskActions.createLink(link);
					if (response.type === types.CREATE_LOCATION_TASK_LINK_SUCCESS) {
						this.updateSavedLinkInList(response.payload.link);
						if (!suppressSuccessNotification) {
							Notification('success', 'Link Added Successfully', '');
						}
						result = true;
					} else if (response.type === types.CREATE_LOCATION_TASK_LINK_FAILURE) {
						Notification('error', 'Error', response.payload.message);
					}
				} else if (link.isEditing) {
					const response = await this.props.locationTaskActions.updateLink(link);
					if (response.type === types.UPDATE_LOCATION_TASK_LINK_SUCCESS) {
						this.updateSavedLinkInList(response.payload.link);
						if (!suppressSuccessNotification) {
							Notification('success', 'Link Saved Successfully', '');
						}
						result = true;
					} else if (response.type === types.UPDATE_LOCATION_TASK_LINK_FAILURE) {
						Notification('error', 'Error', response.payload.message);
					}
				}

				this.setState({ isSavingLink: false });
			} catch (error) {
				Notification('error', 'Error', error);
				this.setState({ isSavingLink: false });
			}
		} else {
			this.setState({ isFormValid: false });
		}
		return result;
	};

	updateSavedLinkInList = (link) => {
		link.isAdding = false;
		link.isEditing = false;
		const links = this.props.task.links.slice();
		const index = links.findIndex((x) => x.ordinal === link.ordinal);
		links.splice(index, 1, link);
		this.setTaskLinks(links);
	};

	anyFilesBeingEdited = () => {
		return this.props.task.files && this.props.task.files.filter((x) => x.isEditing || x.isAdding).length > 0;
	};

	uploadFile = (e) => {
		// For some reason, even though this is a button click, the task list was reloading
		// without e.preventDefault()
		e.preventDefault();
		this.dropzoneRef.current.open();
	};

	setTaskFiles = (files) => {
		this.onTaskFieldChange('files', files);
	};

	setSavingFile = (isSavingFile) => {
		this.setState({ isSavingFile: isSavingFile });
	};

	onDrop = (droppedFiles) => {
		const droppedFile = droppedFiles && droppedFiles.length > 0 ? droppedFiles[0] : null;
		let files = this.props.task.files.slice();
		const newFile = {
			...this.state.defaultFile,
			name: droppedFile.name.substr(0, droppedFile.name.lastIndexOf('.')),
			editing_name: droppedFile.name.substr(0, droppedFile.name.lastIndexOf('.')),
			file: droppedFile,
		};
		files.push(newFile);
		this.setTaskFiles(files);
		this.setState({
			isDropZoneActive: false,
		});
	};

	onDropRejected = () => {
		Notification('error', 'Error', 'Invalid file type(s)');
	};

	onDragEnter = () => {
		this.setState({ isDropZoneActive: true });
	};

	onDragLeave = () => {
		this.setState({ isDropZoneActive: false });
	};

	validateFile = (file) => {
		const isNewFile = file.id === '0';
		const hasFileBytes = file.file && file.file.name && file.file.name.trim().length > 0;
		const hasRequiredName = file.editing_name && file.editing_name.trim().length > 0;
		const isValidNewFile = isNewFile && hasFileBytes && hasRequiredName;

		const isExistingFile = !isNewFile;
		// Is replacing file and file has been chosen, or is not replacing file
		const hasRequiredFileReplacement = (file.showFileInput && hasFileBytes) || !file.showFileInput;
		const isValidExistingFile = isExistingFile && hasRequiredName && hasRequiredFileReplacement;

		return isValidNewFile || isValidExistingFile;
	};

	onSaveFile = async (file, suppressSuccessNotification = false) => {
		let result = false;
		if (this.validateFile(file)) {
			this.setState({ isSavingFile: true });

			const request = {
				...file,
				name: file.editing_name,
				description: file.editing_description,
				fileStatusId: file.editing_fileStatusId,
				locationTaskId: this.props.task.id,
			};

			if (file.isAdding) {
				try {
					const response = await this.props.locationTaskActions.uploadFile(request);
					if (response.type === types.UPLOAD_LOCATION_TASK_FILE_SUCCESS) {
						if (response.payload && response.payload.file) {
							this.handleSavedFile(response.payload.file);
							if (!suppressSuccessNotification) {
								Notification('success', 'Task Updated', 'File has been saved successfully');
							}
							result = true;
						}
					} else if (response.type === types.UPLOAD_LOCATION_TASK_FILE_FAILURE) {
						Notification('error', 'Error', response.payload.message);
						this.setState({ isSavingFile: false });
					}
				} catch (error) {
					Notification('error', 'Error', error);
					this.setState({ isSavingFile: false });
				}
			} else {
				try {
					const response = await this.props.locationTaskActions.updateFile(request);
					if (response.type === types.UPDATE_LOCATION_TASK_FILE_SUCCESS) {
						if (response.payload && response.payload.file) {
							this.handleSavedFile(response.payload.file);
							if (!suppressSuccessNotification) {
								Notification('success', 'Task Updated', 'File has been saved successfully');
							}
							result = true;
						}
					} else if (response.type === types.UPDATE_LOCATION_TASK_FILE_FAILURE) {
						Notification('error', 'Error', response.payload.message);
						this.setState({ isSavingFile: false });
					}
				} catch (error) {
					Notification('error', 'Error', error);
					this.setState({ isSavingFile: false });
				}
			}
		} else {
			this.setState({ isFormValid: false });
		}
		return result;
	};

	handleSavedFile = (file) => {
		let files = this.props.task.files.slice();
		const index = files.findIndex((x) => x.isAdding || x.isEditing);
		file.isAdding = false;
		file.isEditing = false;

		// First remove the existing file from its current index
		// and then place it at the beginning of the list, since it was just modified
		files.splice(index, 1);
		files.unshift(file);

		this.setTaskFiles(files);
		this.setSavingFile(false);
	};

	/**
	 * Share Menu
	 */
	menuOptionClick = (e) => {
		// dates need to be in a certain format. User the start date if there is one, otherwise use target date
		let eventDate = this.props.task.startDate ? this.props.task.startDate : this.props.task.targetDate;
		eventDate = Moment.utc(eventDate).format('YYYYMMDDTHHmmssZ').replace('+00:00', 'Z');

		// get the responsible user name if there is one
		let responsibleUserName = '';
		if (this.props.task.responsibleUserId) {
			let responsibleUser = this.props.filters.responsibleUsers.find((m) => m.id === this.props.task.responsibleUserId);
			if (responsibleUser) {
				responsibleUserName = responsibleUser.name;
			}
		}

		if (e.item.value === SHARE_MENU_ITEMS.AddToOutlook.id) {
			const url = buildOutlookCalendarICSFile(this.props.task, eventDate, responsibleUserName);
			const filename = 'download.ics';
			let blob = new Blob([url], { type: 'text/calendar;charset=utf-8' });
			/****************************************************************
			// many browsers do not properly support downloading data URIs
			// (even with "download" attribute in use) so this solution
			// ensures the event will download cross-browser
			****************************************************************/
			let link = document.createElement('a');
			link.href = window.URL.createObjectURL(blob);
			link.setAttribute('download', filename);
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
		} else if (e.item.value === SHARE_MENU_ITEMS.AddToOutlookcom.id) {
			window.open(buildOutlookCalendarUrl(this.props.task, eventDate, responsibleUserName), '_blank');
		} else if (e.item.value === SHARE_MENU_ITEMS.AddToGoogle.id) {
			window.open(buildGoogleCalendarURL(this.props.task, eventDate, responsibleUserName), '_blank');
		} else if (e.item.value === SHARE_MENU_ITEMS.MailTo.id) {
			window.open(buildMailToUrl(this.props.task, responsibleUserName), '_self');
		} else if (e.item.value === SHARE_MENU_ITEMS.ShareLink.id) {
			// copy to clipboard and show toastr messsage
			Notification('success', '', 'A link to this task has been copied to your clipboard');
		}
	};

	buildShareMenuOptions = () => {
		const menuOptions = [
			{
				label: SHARE_MENU_ITEMS.AddToOutlook.displayName,
				value: SHARE_MENU_ITEMS.AddToOutlook.id,
				command: (e) => {
					this.menuOptionClick(e);
				},
			},
			{
				label: SHARE_MENU_ITEMS.AddToOutlookcom.displayName,
				value: SHARE_MENU_ITEMS.AddToOutlookcom.id,
				command: (e) => {
					this.menuOptionClick(e);
				},
			},
			{
				label: SHARE_MENU_ITEMS.AddToGoogle.displayName,
				value: SHARE_MENU_ITEMS.AddToGoogle.id,
				command: (e) => {
					this.menuOptionClick(e);
				},
			},
			{
				label: SHARE_MENU_ITEMS.MailTo.displayName,
				value: SHARE_MENU_ITEMS.MailTo.id,
				command: (e) => {
					this.menuOptionClick(e);
				},
			},
			{
				label: SHARE_MENU_ITEMS.ShareLink.displayName,
				value: SHARE_MENU_ITEMS.ShareLink.id,
				command: (e) => {
					this.menuOptionClick(e);
				},
			},
		];

		return menuOptions;
	};

	onShareSettingsClick = (e) => {
		this.sharemenu.toggle(e);
	};

	onEditButtonClick = (e) => {
		e.preventDefault();
		this.props.showEditMode();
	};

	onSumbitFeedback = async () => {
		const { feedbackSelection, feedbackText } = this.state;

		if (!feedbackSelection) {
			this.setState({ isFeedbackFormValid: false });
			return;
		}

		try {
			this.setState({ isSubmittingFeedback: true });

			const request = {
				locationTaskId: this.props.task.id,
				feedbackChoice: feedbackSelection,
				text: feedbackText,
			};

			const response = await this.props.locationTaskActions.createFeedback(request);
			if (response.type === types.CREATE_FEEDBACK_SUCCESS) {
				this.setState({
					isSubmittingFeedback: false,
					feedbackSelection: 1,
					feedbackText: '',
					showFeedbackModal: !this.state.showFeedbackModal,
				});
				Notification('success', '', 'Thank you!');
				//this.toggleFeedbackModal();
			} else if (response.type === types.CREATE_FEEDBACK_FAILURE) {
				Notification('error', 'Error', response.payload.message);
				this.setState({ isSubmittingFeedback: false });
			}
		} catch (error) {
			Notification('error', 'Error', error);
			this.setState({ isSubmittingFeedback: false });
		}
	};

	onLocationTaskTranslation = async () => {
		try {
			this.setState({ isTranslatingLocationTask: true });

			const request = {
				locationTaskId: this.props.task.id,
				language: this.state.userLanguage,
			};

			const response = await this.props.locationTaskActions.translateText(request);

			if (response.type == types.TRANSLATE_TEXT_SUCCESS) {
				Notification('success', '', 'The location task was translated');
				this.props.task.translation = response.payload.locationTask;
				this.setState({ isTranslatingLocationTask: false, translationRating: response.payload.locationTask.currentUserRating });
			} else if (response.type == types.TRANSLATE_TEXT_FAILURES) {
				Notification('error', 'Error', response.payload.message);
				this.setState({ isTranslatingLocationTask: false });
			}
		} catch (error) {
			Notification('error', 'Error', error);
			this.setState({ isTranslatingLocationTask: false });
		}
	};

	onLocationTaskTranslationRating = async (translationId, rating) => {
		this.setState({ translationRating: rating });

		try {
			const request = { translationId, rating };

			await this.props.locationTaskActions.translationRating(request);
		} catch (error) {
			Notification('error', 'Error', error);
		}
	};

	toggleFeedbackModal = (e) => {
		e.preventDefault();
		this.setState({ showFeedbackModal: !this.state.showFeedbackModal });
	};

	onFeedbackSelectionChange = (value) => {
		this.setState({ feedbackSelection: value, isFeedbackFormValid: true });
	};

	onFeedbackTextChange = (e) => {
		this.setState({ feedbackText: e.target.value, isFeedbackFormValid: true });
	};

	onCopyToNewTicket = async () => {
		try {
			// Check if supportActions is available
			if (!this.props.supportActions?.createSubscriptionTicket) {
				Notification('error', 'Feature Unavailable', 'Support ticket creation is not available in this context.');
				return;
			}

			this.setState({ isCopyingToNewTicket: true });

			// Create a new support ticket from the feedback task details
			const ticketData = {
				subject: `Feedback: ${this.props.task.name || 'Task Feedback'}`,
				description: this.props.task.description || this.props.task.additionalInfo || 'Feedback support ticket copied from task',
				locationId: this.props.task.locationId,
				projectId: this.props.task.projectId,
				// Copy any additional relevant task details
				taskId: this.props.task.id,
				originalTaskName: this.props.task.name,
			};

			// Use the same action as regular support ticket creation
			const response = await this.props.supportActions.createSubscriptionTicket(ticketData);
			
			if (response.type === types.CREATE_SUBSCRIPTION_TICKET_SUCCESS) {
				Notification('success', 'New Ticket Created', 'A new support ticket has been created from this feedback.');
				
				// Close current drawer
				this.props.onCancel();
				
				// Open the new ticket drawer - this would need to be passed as a prop
				if (this.props.onOpenNewTicket && response.payload.ticketId) {
					this.props.onOpenNewTicket(response.payload.ticketId);
				}
			} else if (response.type === types.CREATE_SUBSCRIPTION_TICKET_FAILURE) {
				Notification('error', 'Error Creating Ticket', response.payload.message);
			}
		} catch (error) {
			Notification('error', 'Error', error);
		} finally {
			this.setState({ isCopyingToNewTicket: false });
		}
	};

	validateTranslation = async () => {
		const userLanguage = detectBrowserLanguage();
		this.setState({ userLanguage });

		try {
			const response = await this.props.locationTaskActions.validateTranslation(this.props.task.id, userLanguage);

			if (response.type == types.VALIDATE_TRANSLATION_TEXT_SUCCESS) {
				this.props.task.translation = response.payload.locationTask;
				this.setState({ canTranslate: response.payload.canTranslate, translationLabels: response.payload.labels });
			} else if (response.type == types.VALIDATE_TRANSLATION_TEXT_FAILURES) {
				this.setState({ canTranslate: false });
			}
		} catch (error) {
			this.setState({ canTranslate: false });
		}
	};

	render() {
		const shareMenuOptions = this.buildShareMenuOptions();
		let menuButtons = [];

		if (this.props.isMobile === false) {
			const taskListUrl = window.location.origin + '/tasks?t=' + this.props.task.id + '&l=' + this.props.task.locationKey;
			const shareButton = (
				<CopyToClipboard key='settings' text={taskListUrl} options={{ format: 'text/plain' }}>
					<div className='menuButtonGroup'>
						<button name='settings' type='button' className='btn' onClick={(e) => this.sharemenu.toggle(e)} alt='config'>
							<Icon name='share' />
						</button>
						<div className='menuButtonTitle'>Share</div>
					</div>
				</CopyToClipboard>
			);
			menuButtons.unshift(shareButton);
		}

		// If this is a feedback support ticket and supportActions is available, add Copy to New Ticket button
		if (this.props.task.isFeedbackSupportTicket && this.props.supportActions?.createSubscriptionTicket) {
			const copyToNewTicketButton = (
				<div key='copyToNewTicket' className='menuButtonGroup'>
					<button 
						name='copyToNewTicket' 
						type='button' 
						className='btn' 
						onClick={this.onCopyToNewTicket} 
						title='Copy to new ticket'
						disabled={this.state.isCopyingToNewTicket}
					>
						<Icon name='copy' />
					</button>
					<div className='menuButtonTitle'>Copy to new ticket</div>
				</div>
			);
			menuButtons.unshift(copyToNewTicketButton);
		}

		// If this is a Location Task (no baseline task ID) then prepend
		// an edit button to the custom drawer menu buttons
		if (!this.props.task.baselineTaskId) {
			const editButton = (
				<div key='edit' className='menuButtonGroup'>
					<button name='edit' type='button' className='btn' onClick={this.onEditButtonClick} title='Edit Task'>
						<Icon name='pencil-alt' />
					</button>
					<div className='menuButtonTitle'>Edit</div>
				</div>
			);
			menuButtons.unshift(editButton);
		}

		let title = (
			<div className='row'>
				<div className='d-flex align-items-center'>
					{this.props.filters.isStartDateActive && this.props.task.startDate && (
						<>
							{this.state.isEditStartDate ? (
								<div className='picker picker-left me-2'>
									<DatePicker
										showIcon
										value={this.props.task.startDate}
										name='startStaticDate'
										label=''
										onChange={this.onStartDateChange}
									/>
								</div>
							) : (
								<div
									className='readonly-date-view startDate me-2 d-flex align-items-center'
									onClick={this.editStartDate}
									style={{ cursor: 'pointer' }}>
									<Icon name='calendar-alt' />
									<span className='ms-2'>{this.props.task.startDate}</span>
								</div>
							)}

							<div className='col-auto d-flex align-items-center me-2'>
								<div className='dash'>
									<Icon name='minus' />
								</div>
							</div>
						</>
					)}

					{/* {this.props.filters.isStartDateActive && (
						<div className='col-auto d-flex align-items-center me-2'>
							<div className='dash'>
								<Icon name='minus' />
							</div>
						</div>
					)} */}

					{this.state.isEditTargetDate && this.props.filters.allowLocationTaskDateChanges ? (
						<div className='picker picker-left'>
							<DatePicker showIcon value={this.props.task.targetDate} name='targetDate' label='' onChange={this.onTargetDateChange} />
						</div>
					) : (
						<div className='readonly-date-view d-flex align-items-center' onClick={this.editTargetDate} style={{ cursor: 'pointer' }}>
							<Icon name='calendar-alt' />
							<span className='ms-2'>{this.props.task.targetDate}</span>
						</div>
					)}
				</div>
			</div>
		);

		const hasBaselineTaskLink = this.props.task && this.props.task.links && this.props.task.links.some((link) => link.baselineTaskLinkId > 0);

		const radioGroupOptions = [
			{ value: FEEDBACK_OPTIONS[0].id, label: FEEDBACK_OPTIONS[0].label },
			{ value: FEEDBACK_OPTIONS[1].id, label: FEEDBACK_OPTIONS[1].label },
			{ value: FEEDBACK_OPTIONS[2].id, label: FEEDBACK_OPTIONS[2].label },
			{ value: FEEDBACK_OPTIONS[3].id, label: FEEDBACK_OPTIONS[3].label },
		];

		if (hasBaselineTaskLink) {
			radioGroupOptions.push({ value: FEEDBACK_OPTIONS[4].id, label: FEEDBACK_OPTIONS[4].label });
		}

		const modalBody = (
			<div>
				<p>
					Your feedback helps keep tasks accurate. Individuals who maintain this task can update the task for future locations. If you need
					a response, please create a Support Ticket.
				</p>
				<form className={this.state.isFeedbackFormValid ? '' : 'was-validated'}>
					<div className='row mx-auto' style={{ width: '80%' }}>
						<RadioGroup
							defaultValue={this.state.feedbackSelection.toString()}
							value={this.state.feedbackSelection.toString()}
							options={radioGroupOptions}
							onValueChange={this.onFeedbackSelectionChange}
						/>
						<div className='col-12 pt-2'>
							<TextArea
								rows={2}
								name=''
								label='Feedback Comment'
								value={this.state.feedbackText}
								isRequired={false}
								onChange={this.onFeedbackTextChange}
							/>
						</div>
					</div>
				</form>
			</div>
		);

		const modalFooter = (
			<div className='mx-auto'>
				<button type='button' className='btn btn-primary me-3' data-dismiss='modal' onClick={this.onSumbitFeedback}>
					Submit
				</button>
				<button type='button' className='btn btn-light btn-outline-dark' data-dismiss='modal' onClick={this.toggleFeedbackModal}>
					Cancel
				</button>
			</div>
		);

		return (
			<div className='limited-mode-task-detail-drawer' ref={this.menuParentRef}>
				<Menu
					model={shareMenuOptions}
					popup
					ref={(el) => (this.sharemenu = el)}
					appendTo={this.menuParentRef.current}
					autoZIndex={false}
					baseZIndex={20001}
				/>
				<Drawer
					isOpen={true}
					isWide={true}
					isMobile={this.props.isMobile}
					title={title}
					onSaveClick={this.onSave}
					saveText='Save'
					onCancelClick={this.onCancelClick}
					menuButtons={menuButtons}
					isSaveDisabled={this.state.isSaving}>
					<LoadingIndicator isLoading={this.state.isSaving || this.props.isLoading} isFullscreen={true}>
						<DropZone
							ref={this.dropzoneRef}
							noClick={true}
							disabled={this.anyFilesBeingEdited()}
							accept={{
								'image/jpeg': ['.jpeg', '.jpg'],
								'image/png': ['.png'],
								'image/gif': ['.gif'],
								'application/postscript': ['.eps'],
								'application/pdf': ['.pdf'],
								'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
								'application/msword': ['.doc'],
								'text/plain': ['.txt', '.rtf'],
								'application/x-zip-compressed': ['.zip'],
								'application/vnd.apple.keynote': ['.keynote'],
								'application/vnd.apple.pages': ['.pages'],
								'application/vnd.apple.numbers': ['.numbers'],
								'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
								'application/vnd.ms-excel': ['.xls'],
								'text/csv': ['.csv'],
								'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
								'application/vnd.ms-powerpoint': ['.ppt'],
								'application/x-rar-compressed': ['.rar'],
								'application/acad': ['.dwg'],
							}}
							multiple={false}
							className='dropzone'
							onDropAccepted={this.onDrop}
							onDropRejected={this.onDropRejected}
							onDragEnter={this.onDragEnter}
							onDragLeave={this.onDragLeave}>
							{({ getRootProps, getInputProps }) => (
								<>
									<div {...getRootProps()} className={`dropzone ${this.state.isDropZoneActive ? 'active' : ''}`}>
										<input {...getInputProps()} />
										{this.state.isDropZoneActive && (
											<div className='dropzone-overlay'>
												<div className='overlay-content'>
													<Icon name='cloud-upload-alt' />
													<p>Drop file here to upload</p>
												</div>
											</div>
										)}
									</div>
									<LimitedModeTaskDetailDrawerForm
										task={this.props.task}
										isShowingBasicInfo={this.state.isShowingBasicInfo}
										isFormValid={this.state.isFormValid}
										isCommentsLoading={this.state.isCommentsLoading}
										onComplete={this.props.onComplete}
										isLoadingProjectCoaches={this.state.isLoadingProjectCoaches}
										currentUserId={this.props.userId}
										customerRole={this.props.customerRole}
										onTaskChange={this.props.onTaskChange}
										onShowHistory={this.onShowHistory}
										saveCommentClick={this.saveCommentClick}
										onShowMoreComments={this.onShowMoreComments}
										onDragEnd={this.onDragEnd}
										toggleStepCompleted={this.toggleStepCompleted}
										onUpdateStepName={this.onUpdateStepName}
										onAddStep={this.onAddStep}
										toggleStepEdit={this.toggleStepEdit}
										toggleCertified={this.toggleCertified}
										toggleEditProjectCoach={this.toggleEditProjectCoach}
										onToggleBasicInfo={this.onToggleBasicInfo}
										toggleFeedBackModal={this.toggleFeedbackModal}
										showContingentDrawer={this.props.showContingentDrawer}
										showPrereqDrawer={this.props.showPrereqDrawer}
										allowLocationTaskBudgetEntry={this.props.filters.allowLocationTaskBudgetEntry}
										allowLocationTaskDateChanges={this.props.filters.allowLocationTaskDateChanges}
										isProjectWidgetActive={this.props.filters.isProjectWidgetActive}
										isTaskFeedbackActive={this.props.filters.isTaskFeedbackActive}
										isTaskFinancialsActive={this.props.filters.isTaskFinancialsActive}
										isTaskStepsActive={this.props.filters.isTaskStepsActive}
										projectCoaches={this.state.projectCoaches}
										mentionableUsers={this.props.filters.mentionableUsers}
										responsibleUsers={this.props.responsibleUsers}
										statuses={this.props.filters.statuses}
										taskTags={this.props.filters.taskTags}
										isSavingLink={this.state.isSavingLink}
										isSavingFile={this.state.isSavingFile}
										locationTaskActions={this.props.locationTaskActions}
										filesActions={this.props.filesActions}
										setTaskLinks={this.setTaskLinks}
										onSaveLink={this.onSaveLink}
										setSavingLink={this.setSavingLink}
										uploadFile={this.uploadFile}
										setTaskFiles={this.setTaskFiles}
										onSaveFile={this.onSaveFile}
										setSavingFile={this.setSavingFile}
										isMobile={this.props.isMobile}
										canTranslate={this.state.canTranslate}
										locationTaskTranslation={this.props.task.translation}
										locationTaskTranslationRating={this.state.translationRating}
										onLocationTaskTranslation={this.onLocationTaskTranslation}
										onLocationTaskTranslationRating={this.onLocationTaskTranslationRating}
										isTranslatingLocationTask={this.state.isTranslatingLocationTask}
										translationLabels={this.state.translationLabels}
									/>
								</>
							)}
						</DropZone>
					</LoadingIndicator>
				</Drawer>
				{this.state.showUnsavedCommentModal && (
					<Modal
						id='unsavedCommentModal'
						style={{ zIndex: 30000 }}
						title='Unsaved Comment'
						body={<p>You have a comment that has not been posted. What would you like to do?</p>}
						onCloseClick={() => { if (!this.state.isCommentsLoading) this.setState({ showUnsavedCommentModal: false }); }}
						isLoading={this.state.isCommentsLoading}
						customFooter={
							<div className='mx-auto'>
								<button type='button' className='btn btn-primary me-3' onClick={this.onSaveCommentAndClose} disabled={this.state.isCommentsLoading}>
									Post &amp; Close
								</button>
								<button type='button' className='btn btn-danger me-3' onClick={this.onDiscardCommentAndClose} disabled={this.state.isCommentsLoading}>
									Discard &amp; Close
								</button>
								<button type='button' className='btn btn-light btn-outline-dark' onClick={() => this.setState({ showUnsavedCommentModal: false })} disabled={this.state.isCommentsLoading}>
									Go Back
								</button>
							</div>
						}
					/>
				)}
				{this.state.showFeedbackModal && (
					<Modal
						id='feedbackModal'
						style={{ zIndex: 30000 }}
						title='Provide Task Feedback'
						body={modalBody}
						onCloseClick={this.toggleFeedbackModal}
						customFooter={modalFooter}
						isLoading={this.state.isSubmittingFeedback}
					/>
				)}
			</div>
		);
	}
}

LimitedModeTaskDetailDrawer.propTypes = {
	// Task
	task: PropTypes.object.isRequired,
	filters: PropTypes.object.isRequired,
	responsibleUsers: PropTypes.array.isRequired,
	isLoading: PropTypes.bool.isRequired,

	// User
	userId: PropTypes.number.isRequired,
	customerRole: PropTypes.string.isRequired,

	// State changing behavior
	onTaskChange: PropTypes.func.isRequired,
	loadComments: PropTypes.func.isRequired,
	loadHistory: PropTypes.func.isRequired,

	// API Access
	locationTaskActions: PropTypes.object.isRequired,
	filesActions: PropTypes.object.isRequired,
	supportActions: PropTypes.object,

	// Drawer behavior
	showContingentDrawer: PropTypes.func,
	showPrereqDrawer: PropTypes.func,
	showEditMode: PropTypes.func.isRequired,
	onComplete: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired,
	onOpenNewTicket: PropTypes.func,

	// funky stuff
	isMobile: PropTypes.bool.isRequired,
};

export default LimitedModeTaskDetailDrawer;
